/**
 * ============================================================================
 * ADD TO SHOPPING MODAL v3.2 - COMPACT + BUTTON STRATEGY COMPLIANCE
 * ============================================================================
 * 
 * 🔧 v3.1: BUTTON STRATEGY COMPLIANCE (5 Fixes)
 *   FIX 1: closeBtn → L4 Mini (32px + 44px touch)
 *   FIX 2: cancelBtn → L2 Secondary (44px, 12px, Teal border)
 *   FIX 3: submitBtn → L1 Primary (52px, 14px, gradient+shadow)
 *   FIX 4: Duplicate Action Buttons → Chip (36px, pill, toggle)
 *   FIX 5: Bulk Action Buttons → Chip (36px, pill)
 *
 * ✅ v3.0: TOKEN ALS PROP
 * ✅ v2.1: SHOPPING SYNC + CSS FIX
 * ✅ v2.0: PLANIVIO REBRANDING
 * 
 * @version 3.2.0 - Compact - Button Strategy Compliance
 */

import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://homem-backend.delightfulpebble-aec8e2aa.northeurope.azurecontainerapps.io';
const LAST_LIST_KEY = 'planivio_last_shopping_list';

console.log('🛒 AddToShoppingModal.js v3.1 loaded - BUTTON STRATEGY COMPLIANCE 🎯');

// ✅ PLANIVIO FARBEN
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  warm: '#E8976C',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#FFFFFF',
  text: '#1F2937',
  textMuted: '#6B7280',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  gradientHover: 'linear-gradient(135deg, #3D7A8C 0%, #5A9680 100%)',
  shadow: 'rgba(74, 144, 164, 0.15)',
  shadowHover: 'rgba(74, 144, 164, 0.25)',
  shadowStrong: 'rgba(74, 144, 164, 0.3)'
};

// ✅ v3.2: BUTTON STRATEGY TOKENS (korrigiert auf Spec-Werte)
const BTN = {
  primaryH: '52px', primaryR: '14px', primaryPad: '0 28px', primaryFs: '0.95rem',
  primaryShadow: '0 4px 16px rgba(74,144,164,0.25)',
  secondaryH: '44px', secondaryR: '12px', secondaryPad: '0 20px', secondaryFs: '0.9rem',
  iconMiniSize: '32px', iconMiniR: '8px',
  chipH: '36px', chipR: '100px', chipFs: '0.82rem',
  fw: 600,
  gap: '12px',
};

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================
const AddToShoppingModal = ({ 
  recipe, 
  onClose, 
  onSuccess, 
  onRecipeCopied,
  token,
  t = (key) => key
}) => {
  
  // State
  const [servings, setServings] = useState(recipe?.servings || 4);
  const [mode, setMode] = useState('existing');
  const [newListName, setNewListName] = useState(`🍳 ${recipe?.name || 'Rezept'} (${recipe?.servings || 4} Port.)`);
  const [selectedListId, setSelectedListId] = useState(null);
  const [activeLists, setActiveLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLists, setLoadingLists] = useState(true);
  const [error, setError] = useState(null);
  
  // Duplikate State
  const [duplicates, setDuplicates] = useState(null);
  const [duplicateActions, setDuplicateActions] = useState({});
  
  // ============================================
  // LOAD ACTIVE LISTS
  // ============================================
  useEffect(() => {
    const loadActiveLists = async () => {
      if (!token) {
        console.warn('❌ AddToShoppingModal: No token available');
        setLoadingLists(false);
        setMode('new');
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/shopping/lists/active`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setActiveLists(data.lists || []);
          
          const lastListId = localStorage.getItem(LAST_LIST_KEY);
          if (lastListId && data.lists?.some(l => l.id === parseInt(lastListId))) {
            setSelectedListId(parseInt(lastListId));
          } else if (data.lists?.length > 0) {
            setSelectedListId(data.lists[0].id);
          } else {
            setMode('new');
          }
        }
      } catch (err) {
        console.error('❌ Error loading lists:', err);
        setMode('new');
      } finally {
        setLoadingLists(false);
      }
    };
    
    loadActiveLists();
  }, [token]);
  
  useEffect(() => {
    if (mode === 'new') {
      setNewListName(`🍳 ${recipe?.name || 'Rezept'} (${servings} Port.)`);
    }
  }, [servings, recipe?.name, mode]);
  
  // ============================================
  // COPY COMMUNITY RECIPE
  // ============================================
  const copyRecipeToMyRecipes = async () => {
    if (!token) throw new Error('Kein Token verfügbar');
    
    console.log('📚 Kopiere Community Rezept:', recipe.name);
    
    const recipeData = {
      name: recipe.name,
      description: recipe.description,
      category: recipe.category || 'Hauptgericht',
      servings: parseInt(servings),
      prepTime: recipe.prep_time || 30,
      cookTime: recipe.cook_time || 0,
      difficulty: recipe.difficulty || 'Mittel',
      tags: recipe.tags || [],
      ingredients: (recipe.ingredients || []).map(ing => ({
        name: ing.name || '',
        quantity: ing.quantity || '',
        unit: ing.unit || '',
        category: ing.category || 'Sonstiges'
      })),
      instructions: (recipe.instructions || []).map(inst => 
        typeof inst === 'string' ? inst : inst.instruction || ''
      ),
      image_url: recipe.image_url
    };
    
    const response = await fetch(`${API_BASE_URL}/api/recipes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(recipeData)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Copy error:', response.status, errorText);
      throw new Error(`Rezept konnte nicht kopiert werden: ${response.status}`);
    }
    
    const result = await response.json();
    const newRecipe = result.recipe || result;
    
    console.log('✅ Rezept kopiert:', newRecipe.name, 'ID:', newRecipe.id);
    if (onRecipeCopied) onRecipeCopied(newRecipe);
    return newRecipe;
  };
  
  // ============================================
  // SUBMIT HANDLER
  // ============================================
  const handleSubmit = useCallback(async (overrideDuplicateActions = null) => {
    if (!token) { setError('Kein Token verfügbar'); return; }
    
    setLoading(true);
    setError(null);
    
    try {
      let recipeId = recipe.id;
      let copiedRecipe = null;
      
      if (recipe.isCommunity) {
        console.log('🌍 Community Rezept erkannt - kopiere zuerst...');
        copiedRecipe = await copyRecipeToMyRecipes();
        recipeId = copiedRecipe.id;
      }
      
      const requestBody = {
        servings: parseInt(servings),
        listId: mode === 'existing' ? selectedListId : null,
        listName: mode === 'new' ? newListName : null,
        duplicateActions: overrideDuplicateActions || duplicateActions
      };
      
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipeId}/to-shopping`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      
      if (!response.ok) throw new Error(result.error || 'Fehler beim Hinzufügen');
      
      if (result.status === 'duplicates_found') {
        setDuplicates(result.duplicates);
        setLoading(false);
        return;
      }
      
      if (result.list?.id) localStorage.setItem(LAST_LIST_KEY, String(result.list.id));
      
      window.dispatchEvent(new CustomEvent('shopping-list-updated', { 
        detail: { listId: result.list?.id, listName: result.list?.name, itemsAdded: result.added?.length || 0, source: 'recipe' } 
      }));
      
      if (onSuccess) onSuccess({ ...result, copiedRecipe });
      onClose();
      
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [recipe, servings, mode, selectedListId, newListName, duplicateActions, token, onSuccess, onClose]);
  
  // ============================================
  // DUPLICATE HANDLERS
  // ============================================
  const handleDuplicateAction = (itemName, action) => {
    setDuplicateActions(prev => ({ ...prev, [itemName]: action }));
  };
  
  const handleApplyToAllDuplicates = (action) => {
    const newActions = {};
    duplicates.forEach(dup => { newActions[dup.name] = action; });
    setDuplicateActions(newActions);
  };
  
  const handleResolveDuplicates = () => {
    const allResolved = duplicates.every(dup => duplicateActions[dup.name]);
    if (!allResolved) { setError('Bitte wähle für alle Duplikate eine Aktion'); return; }
    handleSubmit(duplicateActions);
  };
  
  // ============================================
  // ✅ v3.1: BUTTON STRATEGY STYLES
  // ============================================
  const isCommunity = recipe?.isCommunity;
  
  // 🔧 FIX 1: closeBtn → L4 Mini (32px + 44px touch)
  const closeBtnStyle = {
    width: BTN.iconMiniSize,
    height: BTN.iconMiniSize,
    padding: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: BTN.iconMiniR,
    fontSize: '1rem',
    color: PLANIVIO.textMuted,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box',
    flexShrink: 0,
    position: 'relative',
    transition: 'all 0.2s ease',
    minWidth: '44px',
    minHeight: '44px',
  };

  // 🔧 FIX 2: cancelBtn → L2 Secondary (44px, 12px, Teal border)
  const cancelBtnStyle = {
    height: BTN.secondaryH,
    padding: BTN.secondaryPad,
    background: 'white',
    color: PLANIVIO.teal,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: PLANIVIO.teal,
    borderRadius: BTN.secondaryR,
    fontSize: BTN.secondaryFs,
    fontWeight: BTN.fw,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  };

  // 🔧 FIX 3: submitBtn → L1 Primary (52px, 14px, gradient+shadow)
  const submitBtnStyle = {
    height: BTN.primaryH,
    padding: BTN.primaryPad,
    background: PLANIVIO.gradient,
    color: 'white',
    border: 'none',
    borderRadius: BTN.primaryR,
    fontSize: BTN.primaryFs,
    fontWeight: BTN.fw,
    cursor: 'pointer',
    boxShadow: BTN.primaryShadow,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  };

  // 🔧 FIX 4+5: Chip-Style für Duplicate/Bulk Actions (36px, pill)
  const chipStyle = (isActive, activeColor = PLANIVIO.teal) => ({
    height: BTN.chipH,
    padding: '0 14px',
    borderRadius: BTN.chipR,
    fontSize: BTN.chipFs,
    fontWeight: isActive ? BTN.fw : 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    border: isActive ? `2px solid ${activeColor}` : '1px solid #E2E8F0',
    background: isActive ? activeColor : 'white',
    color: isActive ? 'white' : PLANIVIO.text,
  });

  const styles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem'
    },
    modal: {
      background: PLANIVIO.bgCard, borderRadius: '20px',
      maxWidth: '480px', width: '100%', maxHeight: '90vh', overflow: 'auto',
      borderWidth: '2px', borderStyle: 'solid',
      borderColor: isCommunity ? '#93C5FD' : PLANIVIO.border,
      boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
    },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '1.25rem',
      borderBottomWidth: '2px', borderBottomStyle: 'solid',
      borderBottomColor: isCommunity ? '#93C5FD' : PLANIVIO.border,
      background: isCommunity ? '#EFF6FF' : PLANIVIO.bgLight
    },
    headerTitle: {
      margin: 0, fontSize: '1.1rem', fontWeight: 700, color: PLANIVIO.text,
      display: 'flex', alignItems: 'center', gap: '0.5rem'
    },
    body: { padding: '1.25rem' },
    section: { marginBottom: '1.25rem' },
    label: {
      display: 'block', fontSize: '0.875rem', fontWeight: 600,
      color: PLANIVIO.text, marginBottom: '0.5rem'
    },
    recipeName: {
      padding: '0.75rem 1rem',
      background: isCommunity ? '#EFF6FF' : PLANIVIO.bgLight,
      borderRadius: '10px', fontSize: '0.95rem', color: PLANIVIO.text, fontWeight: 500,
      borderWidth: '1px', borderStyle: 'solid',
      borderColor: isCommunity ? '#93C5FD' : PLANIVIO.borderLight
    },
    communityHint: {
      fontSize: '0.75rem', color: '#3B82F6', marginTop: '0.5rem',
      display: 'flex', alignItems: 'center', gap: '0.25rem'
    },
    input: {
      width: '100%', padding: '0.75rem 1rem',
      borderWidth: '2px', borderStyle: 'solid', borderColor: PLANIVIO.border,
      borderRadius: '10px', fontSize: '0.95rem', outline: 'none',
      background: PLANIVIO.bgLight, color: PLANIVIO.text,
      boxSizing: 'border-box', transition: 'all 0.2s ease'
    },
    select: {
      width: '100%', padding: '0.75rem 1rem',
      borderWidth: '2px', borderStyle: 'solid', borderColor: PLANIVIO.border,
      borderRadius: '10px', fontSize: '0.95rem', outline: 'none',
      background: PLANIVIO.bgLight, color: PLANIVIO.text,
      cursor: 'pointer', boxSizing: 'border-box', transition: 'all 0.2s ease'
    },
    radioGroup: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
    radioOption: {
      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
      padding: '1rem', borderWidth: '2px', borderStyle: 'solid',
      borderColor: PLANIVIO.borderLight, borderRadius: '12px',
      cursor: 'pointer', transition: 'all 0.2s ease', background: PLANIVIO.bgCard
    },
    radioOptionActive: { borderColor: PLANIVIO.teal, background: PLANIVIO.bgLight },
    radioInput: { marginTop: '0.125rem', accentColor: PLANIVIO.teal },
    radioContent: { flex: 1 },
    radioLabel: { fontSize: '0.95rem', fontWeight: 600, color: PLANIVIO.text, marginBottom: '0.25rem' },
    radioHint: { fontSize: '0.8rem', color: PLANIVIO.textMuted },
    // 🔧 FIX 2+3: Footer mit align-items:center (L1+L2 Ausnahme)
    footer: {
      display: 'flex', gap: BTN.gap, padding: '1.25rem',
      borderTopWidth: '2px', borderTopStyle: 'solid',
      borderTopColor: isCommunity ? '#93C5FD' : PLANIVIO.border,
      justifyContent: 'flex-end', alignItems: 'center'
    },
    error: {
      padding: '0.75rem 1rem', background: '#FEF2F2',
      borderWidth: '2px', borderStyle: 'solid', borderColor: '#FECACA',
      borderRadius: '10px', color: '#991B1B', fontSize: '0.875rem', marginBottom: '1rem'
    },
    listInfo: { fontSize: '0.8rem', color: PLANIVIO.textMuted, marginTop: '0.375rem' },
    duplicateList: {
      maxHeight: '300px', overflow: 'auto',
      borderWidth: '2px', borderStyle: 'solid', borderColor: PLANIVIO.border,
      borderRadius: '12px'
    },
    duplicateItem: { padding: '1rem', background: PLANIVIO.bgCard },
    duplicateItemActive: { background: PLANIVIO.bgLight }
  };
  
  // ============================================
  // RENDER: DUPLICATE DIALOG
  // ============================================
  if (duplicates && duplicates.length > 0) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={e => e.stopPropagation()}>
          
          <div style={{ ...styles.header, background: '#FEF3C7', borderBottomColor: '#FCD34D' }}>
            <h2 style={styles.headerTitle}>
              <span>⚠️</span> Artikel bereits vorhanden
            </h2>
            {/* 🔧 FIX 1: L4 Mini */}
            <button 
              style={closeBtnStyle}
              onClick={onClose}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = PLANIVIO.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PLANIVIO.textMuted; }}
            >✕</button>
          </div>
          
          <div style={styles.body}>
            <p style={{ color: PLANIVIO.textMuted, marginBottom: '1rem' }}>
              Einige Artikel sind bereits in der Liste. Was möchtest du tun?
            </p>
            
            {/* 🔧 FIX 5: Bulk Actions → Chip (36px, pill) */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <button
                style={chipStyle(false, '#059669')}
                onClick={() => handleApplyToAllDuplicates('add')}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >➕ Alle addieren</button>
              <button
                style={chipStyle(false, PLANIVIO.teal)}
                onClick={() => handleApplyToAllDuplicates('duplicate')}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >📋 Alle duplizieren</button>
              <button
                style={chipStyle(false, PLANIVIO.textMuted)}
                onClick={() => handleApplyToAllDuplicates('skip')}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
              >⏭️ Alle überspringen</button>
            </div>
            
            {/* Duplikate Liste */}
            <div style={styles.duplicateList}>
              {duplicates.map((dup, idx) => (
                <div 
                  key={dup.name}
                  style={{
                    ...styles.duplicateItem,
                    ...(duplicateActions[dup.name] ? styles.duplicateItemActive : {}),
                    borderBottomWidth: idx < duplicates.length - 1 ? '1px' : '0',
                    borderBottomStyle: 'solid',
                    borderBottomColor: PLANIVIO.borderLight
                  }}
                >
                  <div style={{ fontWeight: 600, color: PLANIVIO.text, marginBottom: '0.5rem' }}>
                    {dup.name}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: PLANIVIO.textMuted, marginBottom: '0.75rem' }}>
                    <span>Vorhanden: <strong style={{ color: PLANIVIO.teal }}>{dup.existing.quantity} {dup.existing.unit}</strong></span>
                    <span style={{ margin: '0 0.5rem' }}>→</span>
                    <span>Neu: <strong style={{ color: PLANIVIO.green }}>{dup.new.quantity} {dup.new.unit}</strong></span>
                  </div>
                  
                  {/* 🔧 FIX 4: Duplicate Actions → Chip (36px, pill, toggle) */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      style={chipStyle(duplicateActions[dup.name] === 'add', PLANIVIO.green)}
                      onClick={() => handleDuplicateAction(dup.name, 'add')}
                    >➕ Addieren</button>
                    <button
                      style={chipStyle(duplicateActions[dup.name] === 'duplicate', PLANIVIO.teal)}
                      onClick={() => handleDuplicateAction(dup.name, 'duplicate')}
                    >📋 Duplikat</button>
                    <button
                      style={chipStyle(duplicateActions[dup.name] === 'skip', PLANIVIO.textMuted)}
                      onClick={() => handleDuplicateAction(dup.name, 'skip')}
                    >⏭️ Skip</button>
                  </div>
                </div>
              ))}
            </div>
            
            {error && <div style={{...styles.error, marginTop: '1rem'}}>{error}</div>}
          </div>
          
          {/* 🔧 FIX 2+3: Footer mit L2 + L1 */}
          <div style={{...styles.footer, borderTopColor: '#FCD34D'}}>
            <button 
              style={cancelBtnStyle}
              onClick={onClose}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >Abbrechen</button>
            <button 
              style={{ ...submitBtnStyle, ...(loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
              onClick={handleResolveDuplicates}
              disabled={loading}
              onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(74,144,164,0.35)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = BTN.primaryShadow; }}
            >{loading ? '⏳' : '✅'} Fortfahren</button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: MAIN MODAL
  // ============================================
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>
            <span>🛒</span> Zur Einkaufsliste hinzufügen
          </h2>
          {/* 🔧 FIX 1: L4 Mini */}
          <button 
            style={closeBtnStyle}
            onClick={onClose}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = PLANIVIO.text; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PLANIVIO.textMuted; }}
          >✕</button>
        </div>
        
        {/* Body */}
        <div style={styles.body}>
          
          {error && <div style={styles.error}>{error}</div>}
          
          {/* Recipe Name */}
          <div style={styles.section}>
            <label style={styles.label}>📖 Rezept</label>
            <div style={styles.recipeName}>
              {isCommunity && <span style={{ marginRight: '0.5rem' }}>🌍</span>}
              {recipe?.name}
            </div>
            {isCommunity && (
              <div style={styles.communityHint}>ℹ️ Wird automatisch in deine Rezepte kopiert</div>
            )}
          </div>
          
          {/* Servings */}
          <div style={styles.section}>
            <label style={styles.label}>👥 Portionen</label>
            <select
              value={servings}
              onChange={e => setServings(parseInt(e.target.value))}
              style={styles.select}
              onFocus={(e) => { e.currentTarget.style.borderColor = PLANIVIO.teal; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74,144,164,0.15)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = PLANIVIO.border; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map(n => (
                <option key={n} value={n}>{n} Portionen</option>
              ))}
            </select>
          </div>
          
          {/* Target List */}
          <div style={styles.section}>
            <label style={styles.label}>📋 Wohin?</label>
            
            <div style={styles.radioGroup}>
              {/* New List */}
              <div 
                style={{ ...styles.radioOption, ...(mode === 'new' ? styles.radioOptionActive : {}) }}
                onClick={() => setMode('new')}
              >
                <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} style={styles.radioInput} />
                <div style={styles.radioContent}>
                  <div style={styles.radioLabel}>🆕 Neue Liste erstellen</div>
                  {mode === 'new' && (
                    <input
                      type="text" value={newListName}
                      onChange={e => setNewListName(e.target.value)}
                      placeholder="Name der Liste..."
                      style={{...styles.input, marginTop: '0.5rem'}}
                      onClick={e => e.stopPropagation()}
                      onFocus={(e) => { e.currentTarget.style.borderColor = PLANIVIO.teal; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74,144,164,0.15)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = PLANIVIO.border; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                  )}
                </div>
              </div>
              
              {/* Existing List */}
              <div 
                style={{ ...styles.radioOption, ...(mode === 'existing' ? styles.radioOptionActive : {}), opacity: activeLists.length === 0 ? 0.5 : 1 }}
                onClick={() => activeLists.length > 0 && setMode('existing')}
              >
                <input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} disabled={activeLists.length === 0} style={styles.radioInput} />
                <div style={styles.radioContent}>
                  <div style={styles.radioLabel}>📂 Zu bestehender Liste</div>
                  {loadingLists ? (
                    <div style={styles.radioHint}>⏳ Lade Listen...</div>
                  ) : activeLists.length === 0 ? (
                    <div style={styles.radioHint}>Keine aktiven Listen vorhanden</div>
                  ) : mode === 'existing' && (
                    <>
                      <select
                        value={selectedListId || ''}
                        onChange={e => setSelectedListId(parseInt(e.target.value))}
                        style={{...styles.select, marginTop: '0.5rem'}}
                        onClick={e => e.stopPropagation()}
                        onFocus={(e) => { e.currentTarget.style.borderColor = PLANIVIO.teal; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74,144,164,0.15)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = PLANIVIO.border; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        {activeLists.map(list => {
                          const isLast = localStorage.getItem(LAST_LIST_KEY) === String(list.id);
                          return <option key={list.id} value={list.id}>{isLast ? '⭐ ' : ''}{list.name} ({list.item_count} Artikel)</option>;
                        })}
                      </select>
                      <div style={styles.listInfo}>📝 {activeLists.length} aktive Liste{activeLists.length !== 1 ? 'n' : ''}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* 🔧 FIX 2+3: Footer mit L2 Secondary + L1 Primary */}
        <div style={styles.footer}>
          <button 
            style={cancelBtnStyle}
            onClick={onClose}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
          >Abbrechen</button>
          <button 
            style={{
              ...submitBtnStyle,
              ...((loading || (mode === 'new' && !newListName.trim())) ? { opacity: 0.5, cursor: 'not-allowed' } : {})
            }}
            onClick={() => handleSubmit()}
            disabled={loading || (mode === 'new' && !newListName.trim())}
            onMouseEnter={(e) => { if (!loading && !(mode === 'new' && !newListName.trim())) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(74,144,164,0.35)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = BTN.primaryShadow; }}
          >{loading ? '⏳ Wird hinzugefügt...' : '🛒 Hinzufügen'}</button>
        </div>
      </div>
    </div>
  );
};

export default AddToShoppingModal;